import { Avatar, Box, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faHatChef } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useDateTime } from "../../../modules/localization/useDateTime"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"

import { ChatMessage, UserType } from "./orderChatTypes"

const useStyles = makeStyles()((theme) => ({
    bubble: {
        borderRadius: ".75rem",
        padding: "1rem",
        gap: "1rem",
        display: "flex",
        flexDirection: "column",
        backgroundColor: tailwindPalette.slate[100],
        color: theme.palette.text.primary,
    },
    bubbleOther: {
        backgroundColor: tailwindPalette.slate[500],
        color: theme.palette.common.white,
    },
    header: {
        display: "flex",
        alignItems: "center",
        gap: ".625rem",
    },
    authorNameOther: {
        color: theme.palette.common.white,
    },
    timestampOther: {
        color: tailwindPalette.slate[300],
    },
    avatar: {
        width: "2.75rem",
        height: "2.75rem",
    },
    avatarSelf: {
        backgroundColor: tailwindPalette.slate[600],
        color: theme.palette.common.white,
    },
    avatarOther: {
        backgroundColor: tailwindPalette.green[600],
        color: theme.palette.common.white,
        fontSize: "1.25rem",
    },
    messageContent: {
        fontSize: ".875rem",
        lineHeight: 1.5,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
}))

function getInitials(displayName: string): string {
    return displayName
        .split(" ")
        .slice(0, 2)
        .map((part) => part[0] ?? "")
        .join("")
        .toUpperCase()
}

type OrderChatMessageBubbleProps = Readonly<{
    message: ChatMessage
    className?: string
}>

export function OrderChatMessageBubble({ message, className }: OrderChatMessageBubbleProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()

    const isCustomer = message.creator.userType === UserType.USER
    const dateTimeString = dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(message.created))

    return (
        <Box className={cx(classes.bubble, !isCustomer && classes.bubbleOther, className)}>
            <Box className={classes.header}>
                <Avatar
                    className={cx(classes.avatar, isCustomer && classes.avatarSelf, !isCustomer && classes.avatarOther)}
                >
                    {isCustomer ? getInitials(message.creator.displayName) : <FontAwesomeIcon icon={faHatChef} />}
                </Avatar>
                <Stack alignItems="flex-start">
                    <Typography variant="body1" className={cx(!isCustomer && classes.authorNameOther)}>
                        {message.creator.displayName}
                    </Typography>
                    <Typography
                        variant="body2"
                        color={isCustomer ? "text.secondary" : undefined}
                        className={cx(!isCustomer && classes.timestampOther)}
                    >
                        {dateTimeString}
                    </Typography>
                </Stack>
            </Box>
            <Typography className={classes.messageContent}>{message.body}</Typography>
        </Box>
    )
}
